// SubscriptionPage.jsx - Simplified with 3 courses and direct payment link
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import httpClient from "../Api/axios";

const SubscriptionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userId, userEmail, userName } = location.state || {};

    const [selectedCourse, setSelectedCourse] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [existingSubscription, setExistingSubscription] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Course options - Only 3 courses
    const courses = [
        {
            id: "course_cyber",
            name: "CyberSecurity Internship",
            duration: "3 Months",
            priceUSD: 108,
            paymentLink: "https://rzp.io/rzp/jpXj97T"
        },
        {
            id: "course_fullstack",
            name: "Fullstack Internship",
            duration: "3 Months",
            priceUSD: 108,
            paymentLink: "https://rzp.io/rzp/jpXj97T"
        },
        {
            id: "course_aiml",
            name: "AI ML Internship",
            duration: "3 Months",
            priceUSD: 108,
            paymentLink: "https://rzp.io/rzp/jpXj97T"
        }
    ];

    // Check if user already has an active subscription
    useEffect(() => {
        const checkExistingSubscription = async () => {
            if (!userId) {
                navigate("/signup");
                return;
            }

            setIsLoading(true);
            try {
                const response = await httpClient.get(`/api/v1/subscriptions/active/${userId}`);
                if (response.data && response.data.hasActiveSubscription) {
                    setExistingSubscription(response.data.subscription);
                    setErrorMessage(`You already have an active subscription for ${response.data.subscription.courseName}. ${response.data.daysRemaining} days remaining.`);
                }
            } catch (err) {
                console.error("Error checking subscription:", err);
            } finally {
                setIsLoading(false);
            }
        };

        checkExistingSubscription();
    }, [userId, navigate]);

    const getEndDate = () => {
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 3);
        return endDate;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Handle payment - Open Razorpay payment link and auto-create subscription
    const handlePayment = async (course) => {
        setSelectedCourse(course);
        setIsProcessing(true);
        setErrorMessage("");
        setSuccessMessage(`Opening payment link for ${course.name}...`);

        // Open Razorpay payment link in new tab
        window.open(course.paymentLink, '_blank');

        // Auto-create subscription after payment (simulate payment completion)
        // In production, you would wait for webhook confirmation
        setTimeout(async () => {
            try {
                const subscriptionData = {
                    userId: userId,
                    userEmail: userEmail,
                    userName: userName,
                    courseId: course.id,
                    courseName: course.name,
                    duration: course.duration,
                    paymentType: "FULL",
                    amount: course.priceUSD,
                    currency: "USD",
                    paymentStatus: "COMPLETED",
                    subscriptionStartDate: new Date().toISOString(),
                    subscriptionEndDate: getEndDate().toISOString(),
                    transactionId: "TXN_" + Date.now() + "_" + Math.random().toString(36).substr(2, 8)
                };

                const response = await httpClient.post("/api/v1/subscriptions/create", subscriptionData, {
                    headers: { "Content-Type": "application/json" }
                });

                if (response.data && response.data.success) {
                    const subscription = response.data.subscription;
                    
                    // Store subscription info in localStorage
                    localStorage.setItem('subscriptionId', subscription.subscriptionId);
                    localStorage.setItem('courseName', course.name);
                    localStorage.setItem('subscriptionStatus', 'active');
                    localStorage.setItem('subscriptionEndDate', getEndDate().toISOString());
                    localStorage.setItem('transactionId', subscription.transactionId);
                    localStorage.setItem('userId', userId);
                    localStorage.setItem('userEmail', userEmail);
                    localStorage.setItem('userName', userName);
                    
                    setSuccessMessage(`✅ Payment Successful! Subscription activated for ${course.name}. Redirecting to login...`);
                    
                    // Redirect to login page after 3 seconds
                    setTimeout(() => {
                        navigate("/signin");
                    }, 3000);
                } else {
                    throw new Error("Failed to create subscription");
                }
            } catch (err) {
                console.error("Subscription error:", err);
                let errorMsg = "Payment verification failed! Please contact support.";
                if (err.response?.status === 409) {
                    errorMsg = "Subscription already exists for this user.";
                }
                setErrorMessage(errorMsg);
                setSuccessMessage("");
            } finally {
                setIsProcessing(false);
            }
        }, 5000); // Wait 5 seconds for payment to be completed
    };

    const handleCourseSelect = (course) => {
        if (existingSubscription) {
            setErrorMessage("You cannot change course while having an active subscription.");
            return;
        }
        setSelectedCourse(course);
        setErrorMessage("");
        setSuccessMessage("");
    };

    const handleBackToSignup = () => {
        navigate("/signup");
    };

    if (isLoading) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.loaderContainer}>
                        <div style={styles.loader}></div>
                        <p>Loading subscription details...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <button onClick={handleBackToSignup} style={styles.backButton}>
                    ← Back to Signup
                </button>

                <h2 style={styles.title}>Choose Your Course</h2>
                <p style={styles.subtitle}>Select a course to start your learning journey</p>
                
                {errorMessage && (
                    <div style={styles.errorMessage}>
                        <span style={styles.errorIcon}>⚠️</span>
                        <span>{errorMessage}</span>
                        <button onClick={() => setErrorMessage("")} style={styles.closeBtn}>×</button>
                    </div>
                )}
                
                {successMessage && (
                    <div style={styles.successMessage}>
                        <span style={styles.successIcon}>✓</span>
                        <span style={{whiteSpace: 'pre-line'}}>{successMessage}</span>
                    </div>
                )}

                {existingSubscription && (
                    <div style={styles.existingSubscriptionCard}>
                        <h4>Your Active Subscription</h4>
                        <div style={styles.existingDetails}>
                            <p><strong>Course:</strong> {existingSubscription.courseName}</p>
                            <p><strong>Payment Type:</strong> {existingSubscription.paymentType}</p>
                            <p><strong>Valid Until:</strong> {formatDate(existingSubscription.subscriptionEndDate)}</p>
                            <p><strong>Transaction ID:</strong> {existingSubscription.transactionId}</p>
                        </div>
                        <button 
                            style={styles.goToDashboardBtn}
                            onClick={() => navigate("/signin")}
                        >
                            Go to Sign In
                        </button>
                    </div>
                )}

                {!existingSubscription && (
                    <>
                        <div style={styles.coursesContainer}>
                            {courses.map((course) => (
                                <div 
                                    key={course.id}
                                    style={{
                                        ...styles.courseCard,
                                        ...(selectedCourse?.id === course.id && styles.selectedCourse)
                                    }}
                                    onClick={() => handleCourseSelect(course)}
                                >
                                    <h3 style={styles.courseTitle}>{course.name}</h3>
                                    <div style={styles.courseDuration}>
                                        Duration: {course.duration}
                                    </div>
                                    <div style={styles.priceBox}>
                                        <span style={styles.priceLabel}>Price</span>
                                        <span style={styles.priceUSD}>${course.priceUSD}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {selectedCourse && (
                            <div style={styles.paymentContainer}>
                                <div style={styles.selectedCourseInfo}>
                                    <h3>Selected Course: {selectedCourse.name}</h3>
                                    <p>Amount to Pay: <strong>${selectedCourse.priceUSD} USD</strong></p>
                                    <p>Click the button below to proceed with payment. After successful payment, you will be redirected to login.</p>
                                </div>
                                
                                <button
                                    style={styles.payNowBtn}
                                    onClick={() => handlePayment(selectedCourse)}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? "Processing..." : `Pay $${selectedCourse.priceUSD} with Razorpay`}
                                </button>
                                
                                <p style={styles.secureNote}>
                                    🔒 Secure payment powered by Razorpay
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#121212",
        padding: "20px",
    },
    card: {
        width: "100%",
        maxWidth: "800px",
        padding: "2rem",
        backgroundColor: "#000",
        borderRadius: "15px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
        color: "white",
        position: "relative",
    },
    backButton: {
        position: "absolute",
        top: "20px",
        left: "20px",
        backgroundColor: "transparent",
        color: "#00bfff",
        border: "1px solid #00bfff",
        borderRadius: "8px",
        padding: "8px 16px",
        cursor: "pointer",
        fontSize: "0.9rem",
        transition: "all 0.3s ease",
        zIndex: 10,
    },
    title: {
        textAlign: "center",
        fontSize: "2rem",
        marginBottom: "0.5rem",
        color: "#fff",
        marginTop: "20px",
    },
    subtitle: {
        textAlign: "center",
        fontSize: "1rem",
        marginBottom: "2rem",
        color: "#ccc",
    },
    errorMessage: {
        backgroundColor: "#dc3545",
        color: "white",
        padding: "12px",
        borderRadius: "8px",
        marginBottom: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "10px",
    },
    successMessage: {
        backgroundColor: "#28a745",
        color: "white",
        padding: "12px",
        borderRadius: "8px",
        marginBottom: "20px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
    },
    errorIcon: {
        fontSize: "20px",
    },
    successIcon: {
        fontSize: "20px",
    },
    closeBtn: {
        background: "none",
        border: "none",
        color: "white",
        fontSize: "24px",
        cursor: "pointer",
    },
    loaderContainer: {
        textAlign: "center",
        padding: "40px",
    },
    loader: {
        border: "4px solid #333",
        borderTop: "4px solid #00bfff",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        animation: "spin 1s linear infinite",
        margin: "0 auto 20px",
    },
    existingSubscriptionCard: {
        backgroundColor: "#1a1a2e",
        border: "2px solid #00bfff",
        borderRadius: "10px",
        padding: "20px",
        marginBottom: "20px",
    },
    existingDetails: {
        marginTop: "15px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    goToDashboardBtn: {
        marginTop: "15px",
        padding: "10px 20px",
        backgroundColor: "#00bfff",
        color: "white",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        width: "100%",
    },
    coursesContainer: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "1.5rem",
        marginBottom: "2rem",
    },
    courseCard: {
        padding: "1.5rem",
        border: "2px solid #333",
        borderRadius: "10px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        backgroundColor: "#111",
        position: "relative",
        textAlign: "center",
    },
    selectedCourse: {
        border: "2px solid #00bfff",
        backgroundColor: "#1a1a2e",
        transform: "scale(1.02)",
        boxShadow: "0 0 20px rgba(0,191,255,0.3)",
    },
    courseTitle: {
        fontSize: "1.1rem",
        textAlign: "center",
        marginBottom: "0.5rem",
        color: "#00bfff",
    },
    courseDuration: {
        fontSize: "0.9rem",
        textAlign: "center",
        marginBottom: "1rem",
        color: "#ccc",
    },
    priceBox: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0.5rem",
        backgroundColor: "#1a1a1a",
        borderRadius: "5px",
        gap: "10px",
    },
    priceLabel: {
        fontSize: "0.9rem",
        color: "#ccc",
    },
    priceUSD: {
        fontSize: "1.3rem",
        fontWeight: "bold",
        color: "#28a745",
    },
    paymentContainer: {
        marginTop: "2rem",
        padding: "1.5rem",
        backgroundColor: "#111",
        borderRadius: "10px",
        border: "1px solid #333",
        textAlign: "center",
    },
    selectedCourseInfo: {
        marginBottom: "1.5rem",
        padding: "1rem",
        backgroundColor: "#1a1a2e",
        borderRadius: "8px",
    },
    payNowBtn: {
        width: "100%",
        padding: "1rem",
        backgroundColor: "#28a745",
        color: "white",
        border: "none",
        borderRadius: "8px",
        fontSize: "1.1rem",
        fontWeight: "bold",
        cursor: "pointer",
        transition: "background-color 0.3s ease",
    },
    secureNote: {
        textAlign: "center",
        marginTop: "1rem",
        fontSize: "0.8rem",
        color: "#888",
    },
    "@media (max-width: 768px)": {
        card: {
            padding: "1.5rem",
        },
        title: {
            fontSize: "1.5rem",
        },
        coursesContainer: {
            gridTemplateColumns: "1fr",
            gap: "1rem",
        },
        backButton: {
            position: "static",
            marginBottom: "20px",
            width: "100%",
        },
    },
    "@media (max-width: 480px)": {
        card: {
            padding: "1rem",
        },
        title: {
            fontSize: "1.3rem",
        },
        subtitle: {
            fontSize: "0.9rem",
        },
        courseTitle: {
            fontSize: "1rem",
        },
        payNowBtn: {
            fontSize: "1rem",
            padding: "0.8rem",
        },
    },
};

// Add keyframe animation for loader
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    @media (max-width: 768px) {
        .backButton {
            position: static !important;
            margin-bottom: 20px;
            width: 100%;
        }
    }
`;
document.head.appendChild(styleSheet);

export default SubscriptionPage;